import { useRouter, useRouterState } from '@tanstack/react-router';

export interface HashTab {
  key: string;
  label: string;
}

export interface HashTabs<T extends string> {
  active: T;
  select: (key: T) => void;
}

/**
 * Which tab of a detail page is showing, kept in the URL fragment.
 *
 * The Vue console put it there and staff link each other at it — "#participants
 * on that cohort" — so the fragment is the state rather than a copy of it. An
 * unknown or missing fragment falls back to the first tab instead of showing
 * nothing, and switching replaces the history entry: tabs on one page are not
 * six places to press Back through.
 *
 * A page that draws its own tab labels passes bare keys; one that hands the
 * list straight to a `TabList` passes `{ key, label }` and gets the same back.
 * `fallback` replaces the first tab as the default, for a page whose sensible
 * opening tab depends on what it shows.
 */
export function useHashTabs<T extends string>(
  tabs: readonly T[] | readonly { key: T }[],
  fallback?: T,
): HashTabs<T> {
  const router = useRouter();
  const hash = useRouterState({ select: (state) => state.location.hash });

  const keys = (tabs as readonly (T | { key: T })[]).map((tab) =>
    typeof tab === 'string' ? tab : tab.key,
  );
  const active = (keys.includes(hash as T) ? hash : (fallback ?? keys[0] ?? '')) as T;

  return {
    active,
    select: (key: T) => void router.navigate({ to: '.', hash: key, replace: true }),
  };
}
